
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderStatistics,
  fetchStateCityStats,
  getOrderList,
} from "../../redux/actions/orderActions";
import { fetchCustomerList } from "../../redux/actions/customerActions";
import DashboardLayout from "../dashboard/DashboardLayout";
import { fetchDashboardData } from "../../redux/actions/dashboardActions";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import SearchBar from "../../Pages/Components/SearchBar";
import MultiSelectDropdown from "../../Pages/Components/MultiSelectDropdown";
import SortableTable from "../../Pages/Components/SortableTable";

/* ─────────────────────────────────────────
   Reusable filter + summary + table layout
───────────────────────────────────────── */
const FilterableTable = ({
  
  searchValue,
  onSearchChange,
  searchPlaceholder,
  dropdowns = [],
  tags = [],
  summaryItems = [],
  tableColumns = [],
  tableData = [],
  renderRow,
  emptyMsg = "No data available.",
  colSpan = 5,
}) => (
  <div>
    {/* Filters row */}
    <div className="w-full flex flex-col md:flex-row items-center justify-start gap-4 mb-4">
      <SearchBar value={searchValue} onChange={onSearchChange} placeholder={searchPlaceholder} />
      {dropdowns.map((d, i) => (
        <MultiSelectDropdown
          key={i}
          placeholder={d.placeholder}
          options={d.options}
          selectedValues={d.selectedValues}
          setSelectedValues={d.setSelectedValues}
        />
      ))}
    </div>

    {/* Active filter tags */}
    {tags.some((t) => t.items.length > 0) && (
      <div className="flex gap-6 flex-wrap mb-4">
        {tags.filter((t) => t.items.length > 0).map((t) => (
          <div key={t.label}>
            <div className="text-sm font-bold text-gray-950 py-1">Selected {t.label}:</div>
            <div className="flex flex-wrap gap-2">
              {t.items.map((item) => (
                <span key={item} className="bg-amber-300 text-gray-950 px-3 py-1 rounded-full text-sm font-medium">
                  {item}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}

    {/* Summary bar */}
    <div className="mb-4 flex justify-between items-center bg-amber-100 p-2 rounded border border-amber-500 shadow-sm">
      {summaryItems.map((s) => (
        <div key={s.label} className="text-sm font-semibold text-gray-950">
          {s.label}: <span className="text-amber-600 ml-2">{s.value}</span>
        </div>
      ))}
    </div>

    {/* Table */}
    <div className="border border-b-amber-500">
      <SortableTable columns={tableColumns} data={tableData} renderRow={renderRow}>
        {tableData.length === 0 && (
          <tr>
            <td colSpan={colSpan} className="text-center text-gray-500 py-4">{emptyMsg}</td>
          </tr>
        )}
      </SortableTable>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════ */
const StateReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedCard, setSelectedCard] = useState(null);
  const [activeList, setActiveList]     = useState("main");

  // States filters
  const [stateSearch, setStateSearch]               = useState("");
  const [stateFilterStates, setStateFilterStates]   = useState([]);

  // Districts filters
  const [districtSearch, setDistrictSearch]                 = useState("");
  const [districtFilterStates, setDistrictFilterStates]     = useState([]);
  const [districtFilterDistricts, setDistrictFilterDistricts] = useState([]);

  // Cities filters
  const [citySearch, setCitySearch]             = useState("");
  const [cityFilterStates, setCityFilterStates] = useState([]);
  const [cityFilterCities, setCityFilterCities] = useState([]);

  // Users filters
  const [userSearch, setUserSearch]               = useState("");
  const [userFilterStates, setUserFilterStates]   = useState([]);
  const [userFilterCities, setUserFilterCities]   = useState([]);
  const [userFilterClients, setUserFilterClients] = useState([]);

  const { userInfo } = useSelector((state) => state.auth);
  const stateStats    = useSelector((state) => state.stateCityStats?.data?.state_stats    || []);
  const cityStats     = useSelector((state) => state.stateCityStats?.data?.city_stats     || []);
  const districtStats = useSelector((state) => state.stateCityStats?.data?.district_stats || []);
  const summary       = useSelector((state) => state.stateCityStats?.data?.summary        || {});
  const rawCustomers  = useSelector((state) => state.fetchCustomerList?.customers);
  const customers = Array.isArray(rawCustomers)
    ? rawCustomers
    : Array.isArray(rawCustomers?.data)
    ? rawCustomers.data
    : [];

  useEffect(() => {
    if (!userInfo?.access_token) {
      navigate("/login");
    } else {
      dispatch(fetchDashboardData(userInfo.access_token));
      dispatch(getOrderStatistics(userInfo.access_token));
      dispatch(fetchStateCityStats(userInfo.access_token));
      dispatch(fetchCustomerList(userInfo.access_token));
      dispatch(getOrderList(userInfo.access_token));
    }
  }, [dispatch, userInfo, navigate]);

  /* ── derived helpers ── */
  const allCities    = useMemo(() => cityStats.filter((c) => c.city?.trim()), [cityStats]);
  const allCustomers = customers;

  const districtCountByState = useMemo(() =>
    districtStats.reduce((acc, d) => { acc[d.state] = (acc[d.state] || 0) + 1; return acc; }, {}),
  [districtStats]);

  const cityCountByState = useMemo(() =>
    cityStats.reduce((acc, c) => { if (c.city?.trim()) acc[c.state] = (acc[c.state] || 0) + 1; return acc; }, {}),
  [cityStats]);

  const userCountByState = useMemo(() =>
    customers.reduce((acc, c) => {
      if (c.state) { if (!acc[c.state]) acc[c.state] = new Set(); acc[c.state].add(c.mobile); }
      return acc;
    }, {}),
  [customers]);

  const userCountByDistrict = useMemo(() =>
    customers.reduce((acc, c) => {
      if (c.district) { if (!acc[c.district]) acc[c.district] = new Set(); acc[c.district].add(c.mobile); }
      return acc;
    }, {}),
  [customers]);

  const userCountByCity = useMemo(() =>
    customers.reduce((acc, c) => {
      if (c.city) { if (!acc[c.city]) acc[c.city] = new Set(); acc[c.city].add(c.mobile); }
      return acc;
    }, {}),
  [customers]);

  const cityToDistrict = useMemo(() =>
    customers.reduce((acc, c) => {
      if (c.city && c.district && !acc[c.city]) acc[c.city] = c.district;
      return acc;
    }, {}),
  [customers]);

  const stateDistrictMap = useMemo(() =>
    districtStats.reduce((acc, d) => {
      if (!acc[d.state]) acc[d.state] = [];
      acc[d.state].push(d);
      return acc;
    }, {}),
  [districtStats]);

  // Add these computed maps alongside your existing useMemo hooks

const successAmountByState = useMemo(() =>
  customers.reduce((acc, c) => {
    if (c.state && c.is_successful) {
      acc[c.state] = (acc[c.state] || 0) + (Number(c.qr_amount) || 0);
    }
    return acc;
  }, {}),
[customers]);

const failureAmountByState = useMemo(() =>
  customers.reduce((acc, c) => {
    if (c.state && c.is_failed) {
      acc[c.state] = (acc[c.state] || 0) + (Number(c.qr_amount) || 0);
    }
    return acc;
  }, {}),
[customers]);

const successAmountByDistrict = useMemo(() =>
  customers.reduce((acc, c) => {
    if (c.district && c.is_successful) {
      acc[c.district] = (acc[c.district] || 0) + (Number(c.qr_amount) || 0);
    }
    return acc;
  }, {}),
[customers]);

const failureAmountByDistrict = useMemo(() =>
  customers.reduce((acc, c) => {
    if (c.district && c.is_failed) {
      acc[c.district] = (acc[c.district] || 0) + (Number(c.qr_amount) || 0);
    }
    return acc;
  }, {}),
[customers]);

const successAmountByCity = useMemo(() =>
  customers.reduce((acc, c) => {
    if (c.city && c.is_successful) {
      acc[c.city] = (acc[c.city] || 0) + (Number(c.qr_amount) || 0);
    }
    return acc;
  }, {}),
[customers]);

const failureAmountByCity = useMemo(() =>
  customers.reduce((acc, c) => {
    if (c.city && c.is_failed) {
      acc[c.city] = (acc[c.city] || 0) + (Number(c.qr_amount) || 0);
    }
    return acc;
  }, {}),
[customers]);

  /* ── totals ── */
  const totalStates    = summary.total_states    ?? stateStats.length;
  const totalDistricts = summary.total_districts ?? districtStats.length;
  const totalCities    = summary.total_cities    ?? allCities.length;
  const totalUsers     = allCustomers.length;

  /* ── overview summary cards ── */
  const summaryCards = [
    { key: "states",    label: "Total States",    value: totalStates,    border: "border-amber-400",  bg: "bg-amber-50",  textColor: "text-amber-600",  hoverBg: "hover:bg-amber-100"  },
    { key: "districts", label: "Total Districts", value: totalDistricts, border: "border-orange-400", bg: "bg-orange-50", textColor: "text-orange-600", hoverBg: "hover:bg-orange-100" },
    { key: "cities",    label: "Total Cities",    value: totalCities,    border: "border-yellow-400", bg: "bg-yellow-50", textColor: "text-yellow-600", hoverBg: "hover:bg-yellow-100" },
    { key: "users",     label: "Total Users",     value: totalUsers,     border: "border-green-400",  bg: "bg-green-50",  textColor: "text-green-600",  hoverBg: "hover:bg-green-100"  },
  ];

  const handleCardClick = (cardKey) => {
    setSelectedCard(cardKey);
    setActiveList("main");
  };

  /* ── sub-card definitions ── */
  const subCardDefs = {
    states: [
      { key: "main",      label: "Total States",    value: totalStates,    border: "border-amber-400",  bg: "bg-amber-50",  textColor: "text-amber-600"  },
      { key: "districts", label: "Total Districts", value: totalDistricts, border: "border-orange-400", bg: "bg-orange-50", textColor: "text-orange-600" },
      { key: "cities",    label: "Total Cities",    value: totalCities,    border: "border-yellow-400", bg: "bg-yellow-50", textColor: "text-yellow-600" },
      { key: "users",     label: "Total Users",     value: totalUsers,     border: "border-green-400",  bg: "bg-green-50",  textColor: "text-green-600"  },
    ],
    districts: [
      { key: "main",   label: "Total Districts", value: totalDistricts, border: "border-orange-400", bg: "bg-orange-50", textColor: "text-orange-600" },
      { key: "cities", label: "Total Cities",    value: totalCities,    border: "border-yellow-400", bg: "bg-yellow-50", textColor: "text-yellow-600" },
      { key: "users",  label: "Total Users",     value: totalUsers,     border: "border-green-400",  bg: "bg-green-50",  textColor: "text-green-600"  },
    ],
    cities: [
      { key: "main",      label: "Total Cities",    value: totalCities,    border: "border-yellow-400", bg: "bg-yellow-50", textColor: "text-yellow-600" },
      { key: "users",     label: "Total Users",     value: totalUsers,     border: "border-green-400",  bg: "bg-green-50",  textColor: "text-green-600"  },
      // { key: "districts", label: "Total Districts", value: totalDistricts, border: "border-orange-400", bg: "bg-orange-50", textColor: "text-orange-600" },
    ],
    users: [
      { key: "main", label: "Total Users", value: totalUsers, border: "border-green-400", bg: "bg-green-50", textColor: "text-green-600" },
    ],
  };

  /* ══════════════════════════════════════
     STATES LIST
  ══════════════════════════════════════ */
  const renderStatesList = () => {
    const uniqueStateNames = [...new Set(stateStats.map((s) => s.state).filter(Boolean))];

    const filtered = stateStats.filter((s) => {
      const matchSearch = s.state?.toLowerCase().includes(stateSearch.toLowerCase());
      const matchState  = stateFilterStates.length === 0 || stateFilterStates.includes(s.state);
      return matchSearch && matchState;
    });

    const totalRedemptions = filtered.reduce((sum, s) => sum + (s.total_redemptions || 0), 0);
   
    return (
      <FilterableTable
        searchValue={stateSearch}
        onSearchChange={setStateSearch}
        searchPlaceholder="Search by State"
        dropdowns={[
          {
            placeholder: "Filter by State",
            options: uniqueStateNames.map((s) => ({ label: s, value: s })),
            selectedValues: stateFilterStates.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setStateFilterStates(opts.map((o) => o.value)),
          },
        ]}
        tags={[{ label: "States", items: stateFilterStates }]}
        summaryItems={[
          { label: "Total States",      value: filtered.length },
          { label: "Total Redemptions", value: totalRedemptions },
       
        ]}
        tableColumns={[
          { key: "state",                  label: "State",             sortable: true },
          { key: "district_count",         label: "Districts",         sortable: true },
          { key: "city_count",             label: "Cities",            sortable: true },
          // { key: "user_count",             label: "User Count",        sortable: true },
          { key: "total_redemptions",      label: "Total Redemptions", sortable: true },
          { key: "successful_redemptions", label: "Success",           sortable: true },
          { key: "failed_redemptions",     label: "Failed",            sortable: true },
          { key: "success_rate",           label: "Success Rate",      sortable: true },
          { key: "total_amount",           label: "Amount",            sortable: true },
        ]}
        tableData={filtered.map((s) => ({
          ...s,
          district_count: districtCountByState[s.state] || 0,
          city_count:     cityCountByState[s.state]     || 0,
          user_count:     userCountByState[s.state]?.size || 0,
        }))}
        renderRow={(s, i) => (
          <tr key={i} className="hover:bg-amber-50 hover:text-gray-950 hover:font-semibold">
            <td className="py-4 px-2 border-b font-medium">{s.state}</td>
            <td className="py-4 px-2 border-b">{s.district_count}</td>
            <td className="py-4 px-2 border-b">{s.city_count}</td>
            {/* <td className="py-4 px-2 border-b">{s.user_count}</td> */}
            <td className="py-4 px-2 border-b">{s.total_redemptions}</td>
            <td className="py-4 px-2 border-b text-green-600">{s.successful_redemptions}</td>
            <td className="py-4 px-2 border-b text-red-500">{s.failed_redemptions}</td>
            <td className="py-4 px-2 border-b">{s.success_rate}%</td>
            <td className="py-4 px-2 border-b">₹{s.total_amount}</td>
          </tr>
        )}
        emptyMsg="No state data available."
        colSpan={9}
      />
    );
  };

  /* ══════════════════════════════════════
     DISTRICTS LIST
  ══════════════════════════════════════ */
  const renderDistrictsList = () => {
    const uniqueDistrictStates    = [...new Set(districtStats.map((d) => d.state).filter(Boolean))];
    const uniqueDistrictNames     = [...new Set(
      districtStats
        .filter((d) => districtFilterStates.length === 0 || districtFilterStates.includes(d.state))
        .map((d) => d.district).filter(Boolean)
    )];

    const filtered = districtStats.filter((d) => {
      const matchSearch   = d.district?.toLowerCase().includes(districtSearch.toLowerCase()) ||
                            d.state?.toLowerCase().includes(districtSearch.toLowerCase());
      const matchState    = districtFilterStates.length    === 0 || districtFilterStates.includes(d.state);
      const matchDistrict = districtFilterDistricts.length === 0 || districtFilterDistricts.includes(d.district);
      return matchSearch && matchState && matchDistrict;
    });

    const totalRedemptions = filtered.reduce((sum, d) => sum + (d.total_redemptions || 0), 0);
    // const totalAmount      = filtered.reduce((sum, d) => sum + (Number(d.total_amount) || 0), 0);
  
    return (
      <FilterableTable
        searchValue={districtSearch}
        onSearchChange={setDistrictSearch}
        searchPlaceholder="Search by District or State"
        dropdowns={[
          {
            placeholder: "Filter by State",
            options: uniqueDistrictStates.map((s) => ({ label: s, value: s })),
            selectedValues: districtFilterStates.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setDistrictFilterStates(opts.map((o) => o.value)),
          },
          {
            placeholder: "Filter by District",
            options: uniqueDistrictNames.map((d) => ({ label: d, value: d })),
            selectedValues: districtFilterDistricts.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setDistrictFilterDistricts(opts.map((o) => o.value)),
          },
        ]}
        tags={[
          { label: "States",    items: districtFilterStates    },
          { label: "Districts", items: districtFilterDistricts },
        ]}
        summaryItems={[
          { label: "Total Districts",   value: filtered.length },
          { label: "Total Redemptions", value: totalRedemptions },
          // { label: "Total Amount",      value: `₹${totalAmount.toFixed(2)}` },
          
        ]}
        tableColumns={[
          { key: "district",               label: "District",          sortable: true },
          { key: "state",                  label: "State",             sortable: true },
          { key: "city_count",             label: "Cities",            sortable: true },
          // { key: "user_count",             label: "User Count",        sortable: true },
          { key: "total_redemptions",      label: "Total Redemptions", sortable: true },
          { key: "successful_redemptions", label: "Success",           sortable: true },
          { key: "failed_redemptions",     label: "Failed",            sortable: true },
          { key: "success_rate",           label: "Success Rate",      sortable: true },
          { key: "total_amount",           label: "Amount",            sortable: true },
        ]}
        tableData={filtered.map((d) => ({
          ...d,
          user_count: userCountByDistrict[d.district]?.size || 0,
        }))}
        renderRow={(d, i) => (
          <tr key={i} className="hover:bg-orange-50 hover:text-gray-950 hover:font-semibold">
            <td className="py-4 px-2 border-b font-medium">{d.district || "-"}</td>
            <td className="py-4 px-2 border-b">{d.state}</td>
            <td className="py-4 px-2 border-b">{d.city_count}</td>
            {/* <td className="py-4 px-2 border-b">{d.user_count}</td> */}
            <td className="py-4 px-2 border-b">{d.total_redemptions}</td>
            <td className="py-4 px-2 border-b text-green-600">{d.successful_redemptions}</td>
            <td className="py-4 px-2 border-b text-red-500">{d.failed_redemptions}</td>
            <td className="py-4 px-2 border-b">{d.success_rate}%</td>
            <td className="py-4 px-2 border-b">₹{d.total_amount}</td>
          </tr>
        )}
        emptyMsg="No district data available."
        colSpan={9}
      />
    );
  };

  /* ══════════════════════════════════════
     CITIES LIST
  ══════════════════════════════════════ */
  const renderCitiesList = () => {
    const uniqueCityStates = [...new Set(allCities.map((c) => c.state).filter(Boolean))];
    const uniqueCityNames  = [...new Set(
      allCities
        .filter((c) => cityFilterStates.length === 0 || cityFilterStates.includes(c.state))
        .map((c) => c.city).filter(Boolean)
    )];

    const filtered = allCities.filter((c) => {
      const matchSearch = c.city?.toLowerCase().includes(citySearch.toLowerCase()) ||
                          c.state?.toLowerCase().includes(citySearch.toLowerCase());
      const matchState  = cityFilterStates.length === 0 || cityFilterStates.includes(c.state);
      const matchCity   = cityFilterCities.length === 0 || cityFilterCities.includes(c.city);
      return matchSearch && matchState && matchCity;
    });

    const totalRedemptions = filtered.reduce((sum, c) => sum + (c.total_redemptions || 0), 0);
   

    return (
      <FilterableTable
        searchValue={citySearch}
        onSearchChange={setCitySearch}
        searchPlaceholder="Search by City or State"
        dropdowns={[
          {
            placeholder: "Filter by State",
            options: uniqueCityStates.map((s) => ({ label: s, value: s })),
            selectedValues: cityFilterStates.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setCityFilterStates(opts.map((o) => o.value)),
          },
          {
            placeholder: "Filter by City",
            options: uniqueCityNames.map((c) => ({ label: c, value: c })),
            selectedValues: cityFilterCities.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setCityFilterCities(opts.map((o) => o.value)),
          },
        ]}
        tags={[
          { label: "States", items: cityFilterStates },
          { label: "Cities", items: cityFilterCities },
        ]}
        summaryItems={[
          { label: "Total Cities",      value: filtered.length },
          { label: "Total Redemptions", value: totalRedemptions },
         
        ]}
        tableColumns={[
          { key: "city",                   label: "City",              sortable: true },
          { key: "district",               label: "District",          sortable: true },
          { key: "state",                  label: "State",             sortable: true },
          // { key: "user_count",             label: "User Count",        sortable: true },
          { key: "total_redemptions",      label: "Total Redemptions", sortable: true },
          { key: "successful_redemptions", label: "Success",           sortable: true },
          { key: "failed_redemptions",     label: "Failed",            sortable: true },
          { key: "success_rate",           label: "Success Rate",      sortable: true },
          { key: "total_amount",           label: "Amount",            sortable: true },
        ]}
        tableData={filtered.map((c) => ({
          ...c,
          district: c.district || cityToDistrict[c.city] || (() => {
            const dists = stateDistrictMap[c.state] || [];
            return dists.length === 1 ? dists[0].district : "-";
          })(),
          user_count: userCountByCity[c.city]?.size || 0,
        }))}
        renderRow={(c, i) => (
          <tr key={i} className="hover:bg-yellow-50 hover:text-gray-950 hover:font-semibold">
            <td className="py-4 px-2 border-b font-medium">{c.city}</td>
            <td className="py-4 px-2 border-b">{c.district}</td>
            <td className="py-4 px-2 border-b">{c.state}</td>
            {/* <td className="py-4 px-2 border-b">{c.user_count}</td> */}
            <td className="py-4 px-2 border-b">{c.total_redemptions}</td>
            <td className="py-4 px-2 border-b text-green-600">{c.successful_redemptions}</td>
            <td className="py-4 px-2 border-b text-red-500">{c.failed_redemptions}</td>
            <td className="py-4 px-2 border-b">{c.success_rate}%</td>
            <td className="py-4 px-2 border-b">₹{c.total_amount}</td>
          </tr>
        )}
        emptyMsg="No city data available."
        colSpan={9}
      />
    );
  };

  /* ══════════════════════════════════════
     USERS LIST
  ══════════════════════════════════════ */
  const renderUsersList = () => {
    const uniqueUserStates  = [...new Set(allCustomers.map((c) => c.state).filter(Boolean))];
    const uniqueUserCities  = [...new Set(
      allCustomers
        .filter((c) => userFilterStates.length === 0 || userFilterStates.includes(c.state))
        .map((c) => c.city).filter(Boolean)
    )];
    const uniqueUserClients = [...new Set(allCustomers.map((c) => c.client_name).filter(Boolean))];

    const filtered = allCustomers.filter((c) => {
      const matchSearch  = c.customer_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
                           c.mobile?.toLowerCase().includes(userSearch.toLowerCase()) ||
                           c.client_name?.toLowerCase().includes(userSearch.toLowerCase());
      const matchState   = userFilterStates.length  === 0 || userFilterStates.includes(c.state);
      const matchCity    = userFilterCities.length  === 0 || userFilterCities.includes(c.city);
      const matchClient  = userFilterClients.length === 0 || userFilterClients.includes(c.client_name);
      return matchSearch && matchState && matchCity && matchClient;
    });

    const totalAmount = filtered.reduce((sum, c) => sum + (Number(c.qr_amount) || 0), 0);
      const SuccessAmount = filtered.reduce((sum, c) => sum + (c.is_successful ? Number(c.qr_amount) || 0 : 0), 0);
  const FailureAmount = filtered.reduce((sum, c) => sum + (!c.is_successful ? Number(c.qr_amount) || 0 : 0), 0);
    return (
      <FilterableTable
        searchValue={userSearch}
        onSearchChange={setUserSearch}
        searchPlaceholder="Search by Customer, Mobile, Client"
        dropdowns={[
          {
            placeholder: "Filter by Clients",
            options: uniqueUserClients.map((c) => ({ label: c, value: c })),
            selectedValues: userFilterClients.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setUserFilterClients(opts.map((o) => o.value)),
          },
          {
            placeholder: "Filter by States",
            options: uniqueUserStates.map((s) => ({ label: s, value: s })),
            selectedValues: userFilterStates.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setUserFilterStates(opts.map((o) => o.value)),
          },
          {
            placeholder: "Filter by City",
            options: uniqueUserCities.map((c) => ({ label: c, value: c })),
            selectedValues: userFilterCities.map((v) => ({ label: v, value: v })),
            setSelectedValues: (opts) => setUserFilterCities(opts.map((o) => o.value)),
          },
        ]}
        tags={[
          { label: "Clients", items: userFilterClients },
          { label: "States",  items: userFilterStates  },
          { label: "Cities",  items: userFilterCities  },
        ]}
        summaryItems={[
          { label: "Total Customers", value: filtered.length },
          
          {label: "Success Amount", value: `₹${SuccessAmount.toFixed(2)}`},
          {label: "Failure Amount", value: `₹${FailureAmount.toFixed(2)}`},
          { label: "Total Amount",    value: `₹${totalAmount.toFixed(2)}` },
        ]}
        tableColumns={[
          { key: "redeemed_at",   label: "Date",        sortable: true },
          { key: "customer_name", label: "Customer",    sortable: true },
          { key: "mobile",        label: "Mobile",      sortable: true },
          { key: "client_name",   label: "Client Name", sortable: true },
          { key: "qr_id",         label: "QR Code",     sortable: true },
          { key: "qr_amount",     label: "Amount",      sortable: true },
          { key: "city",          label: "City",        sortable: true },
          { key: "state",         label: "State",       sortable: true },
          { key: "status",        label: "Status",      sortable: true },
        ]}
        tableData={filtered.map((c) => ({
          ...c,
          status: c.is_successful ? "Success" : c.is_failed ? "Failed" : "Failed",
          redeemed_at: new Date(c.redeemed_at).toLocaleString(),
        }))}
        renderRow={(c, i) => (
          <tr key={c.redemption_id} className="hover:bg-amber-50 hover:text-gray-950 hover:font-semibold">
            <td className="py-4 px-2 border-b">{c.redeemed_at}</td>
            <td className="px-2 py-4 border-b">{c.order_type === "promotional" ? "-" : c.customer_name}</td>
            <td className="px-2 py-4 border-b">{c.order_type === "promotional" ? "-" : c.mobile}</td>
            <td className="py-4 px-2 border-b">{c.client_name}</td>
            <td className="py-4 px-2 border-b">{c.qr_id}</td>
            <td className="py-4 px-2 border-b">{c.qr_amount}</td>
            <td className="py-4 px-2 border-b">{c.city}</td>
            <td className="py-4 px-2 border-b">{c.state}</td>
            <td className="py-4 px-2 border-b">
              <button className={`px-2 text-white rounded-lg font-semibold text-xs ${
                c.status === "Success" ? "bg-green-600" : c.status === "Failed" ? "bg-red-500" : "bg-gray-400"
              }`}>
                {c.status}
              </button>
            </td>
          </tr>
        )}
        emptyMsg="No redeemed customer data available."
        colSpan={9}
      />
    );
  };

  /* ── resolve active list ── */
  const getActiveListConfig = () => {
    const key = activeList === "main" ? selectedCard : activeList;
    switch (key) {
      case "states":    return { title: "States List",    headerBg: "",  content: renderStatesList()    };
      case "districts": return { title: "Districts List", headerBg: "", content: renderDistrictsList() };
      case "cities":    return { title: "Cities List",    headerBg: "", content: renderCitiesList()    };
      case "users":     return { title: "Users List",     headerBg: "",  content: renderUsersList()     };
      default:          return { title: "List",           headerBg: "",   content: null                  };
    }
  };

  const pageTitleMap = {
    states:    "States Overview",
    districts: "Districts Overview",
    cities:    "Cities Overview",
    users:     "Total Users Overview",
  };

  const activeListCfg = selectedCard ? getActiveListConfig() : null;
  const subCards      = selectedCard ? (subCardDefs[selectedCard] || []) : [];

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto px-4 pb-10">

        {/* Header */}
        <div className="flex items-center gap-3 py-4">
          {selectedCard ? (
            <button
              onClick={() => { setSelectedCard(null); setActiveList("main"); }}
              className="hover:text-gray-800 p1 hover:bg-amber-50 rounded-lg transition"
            >
              <FaArrowLeft size={20} />
            </button>
          ) : (
            <button onClick={() => navigate(-1)} className="hover:text-gray-800 p-1">
              <FaArrowLeft size={20} />
            </button>
          )}
          <h2 className="text-2xl font-bold text-amber-600">
            {selectedCard ? pageTitleMap[selectedCard] : "State-wise Redemption Statistics"}
          </h2>
        </div>

        {/* ── OVERVIEW ── */}
        {!selectedCard && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-8">
              {summaryCards.map((card) => (
                <div
                  key={card.key}
                  onClick={() => handleCardClick(card.key)}
                  className={`border-2 ${card.border} ${card.bg} ${card.hoverBg}
                    rounded-2xl p-5 flex flex-col gap-1 shadow-sm cursor-pointer
                    hover:shadow-lg transition-all duration-200 select-none`}
                >
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{card.label}</p>
                  <p className={`text-3xl font-bold ${card.textColor}`}>{card.value}</p>
                </div>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-sm text-amber-800 font-medium">
              💡 Click on any card above to explore detailed breakdowns with sub-counts.
            </div>
          </>
        )}

        {/* ── DETAIL VIEW ── */}
        {selectedCard && (
          <>
            {/* Sub-cards */}
            <div className={`grid grid-cols-2 sm:grid-cols-${subCards.length} gap-4 mb-6`}>
              {subCards.map((card) => (
                <div
                  key={card.key}
                  onClick={() => setActiveList((prev) => (prev === card.key ? "main" : card.key))}
                  className={`border-2 ${card.border} rounded-xl p-4 flex flex-col gap-1 shadow-sm
                    cursor-pointer transition-all duration-200 select-none
                    ${activeList === card.key
                      ? card.bg.replace("50", "200") + " shadow-md"
                      : card.bg + " hover:shadow-md"
                    }`}
                >
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.textColor}`}>{card.value}</p>
                </div>
              ))}
            </div>

            {/* Dynamic List */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-hidden">
              <div className={`px-5 pt-3 ${activeListCfg.headerBg} flex items-center gap-2`}>
                <h3 className="font-bold text-amber-600 text-xl">{activeListCfg.title}</h3>
              </div>
              <div className="p-4">
                {activeListCfg.content}
              </div>
            </div>
          </>
        )}

      </div>
    </DashboardLayout>
  );
};

export default StateReport;